import { useQuery, useMutation, useQueryClient } from 'react-query'
import { useSnackbar } from 'notistack'
import { notificationsAPI } from '../utils/api'

// Hook para obtener configuración de notificaciones
export const useNotificationSettings = () => {
  return useQuery(
    ['notification-settings'],
    () => notificationsAPI.getSettings(),
    {
      staleTime: 10 * 60 * 1000, // 10 minutos
      select: (data) => data
    }
  )
}

// Hook para obtener configuraciones de notificaciones
export const useNotificationConfigs = () => {
  return useQuery(
    ['notification-configs'],
    () => {
      console.log('🔍 DEBUG - useNotificationConfigs - Iniciando petición a API');
      return notificationsAPI.getConfigs();
    },
    {
      staleTime: 5 * 60 * 1000, // 5 minutos
      select: (data) => {
        console.log('🔍 DEBUG - useNotificationConfigs - data recibida:', data);
        console.log('🔍 DEBUG - useNotificationConfigs - data.data:', data?.data);
        console.log('🔍 DEBUG - useNotificationConfigs - data.data.configs:', data?.data?.configs);
        console.log('🔍 DEBUG - useNotificationConfigs - typeof data:', typeof data);
        console.log('🔍 DEBUG - useNotificationConfigs - data keys:', data ? Object.keys(data) : 'data is null/undefined');
        return data?.data?.configs || []
      },
      onError: (error) => {
        console.error('❌ ERROR - useNotificationConfigs:', error);
        console.error('❌ ERROR - useNotificationConfigs - error.response:', error.response);
        console.error('❌ ERROR - useNotificationConfigs - error.message:', error.message);
      },
      onSuccess: (data) => {
        console.log('✅ SUCCESS - useNotificationConfigs - data recibida:', data);
      }
    }
  )
}

// Hook para crear configuración de notificación
export const useCreateNotificationConfig = (onSuccessCallback) => {
  const queryClient = useQueryClient()
  const { enqueueSnackbar } = useSnackbar()

  return useMutation(
    (configData) => notificationsAPI.createConfig(configData),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['notification-configs'])
        enqueueSnackbar('Configuración de notificación creada exitosamente', {
          variant: 'success'
        })
        if (onSuccessCallback) onSuccessCallback()
      },
      onError: (error) => {
        const message = error.response?.data?.message || 'Error al crear configuración'
        enqueueSnackbar(message, { variant: 'error' })
      }
    }
  )
}

// Hook para actualizar configuración de notificación
export const useUpdateNotificationConfig = (onSuccessCallback) => {
  const queryClient = useQueryClient()
  const { enqueueSnackbar } = useSnackbar()

  return useMutation(
    ({ id, configData }) => notificationsAPI.updateConfig(id, configData),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['notification-configs'])
        enqueueSnackbar('Configuración actualizada exitosamente', {
          variant: 'success'
        })
        if (onSuccessCallback) onSuccessCallback()
      },
      onError: (error) => {
        const message = error.response?.data?.message || 'Error al actualizar configuración'
        enqueueSnackbar(message, { variant: 'error' })
      }
    }
  )
}

// Hook para eliminar configuración de notificación
export const useDeleteNotificationConfig = () => {
  const queryClient = useQueryClient()
  const { enqueueSnackbar } = useSnackbar()

  return useMutation(
    (id) => notificationsAPI.deleteConfig(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['notification-configs'])
        enqueueSnackbar('Configuración eliminada exitosamente', {
          variant: 'success'
        })
      },
      onError: (error) => {
        const message = error.response?.data?.message || 'Error al eliminar configuración'
        enqueueSnackbar(message, { variant: 'error' })
      }
    }
  )
}

// Hook para enviar notificación de prueba
export const useSendTestNotification = () => {
  const { enqueueSnackbar } = useSnackbar()

  return useMutation(
    (testData) => notificationsAPI.sendTest(testData),
    {
      onSuccess: (data) => {
        console.log('🔍 DEBUG - useSendTestNotification - data recibida:', data);
        const responseData = data.data || data
        console.log('🔍 DEBUG - useSendTestNotification - valores extraídos:', responseData);
        enqueueSnackbar(`Notificación de prueba enviada a ${responseData.recipients?.length || 0} destinatario(s)`, {
          variant: 'success'
        })
      },
      onError: (error) => {
        const message = error.response?.data?.message || 'Error al enviar notificación de prueba'
        enqueueSnackbar(message, { variant: 'error' })
      }
    }
  )
}

// Hook para obtener logs de notificaciones
export const useNotificationLogs = (params = {}) => {
  return useQuery(
    ['notification-logs', params],
    () => notificationsAPI.getLogs(params),
    {
      staleTime: 30 * 1000, // 30 segundos
      select: (data) => ({
        logs: data.logs || [],
        pagination: data.pagination || {}
      })
    }
  )
}

// Hook para enviar recordatorio semanal manualmente
export const useSendWeeklyReminder = () => {
  const { enqueueSnackbar } = useSnackbar()

  return useMutation(
    () => notificationsAPI.sendWeeklyReminder(),
    {
      onSuccess: (data) => {
        console.log('🔍 DEBUG - useSendWeeklyReminder - data recibida:', data);
        const { invoiceCount, totalAmount, recipients } = data.data || data
        console.log('🔍 DEBUG - useSendWeeklyReminder - valores extraídos:', { invoiceCount, totalAmount, recipients });
        enqueueSnackbar(
          `Recordatorio enviado: ${invoiceCount} facturas por $${totalAmount?.toLocaleString('es-CL')} a ${recipients?.length} destinatario(s)`,
          { variant: 'success' }
        )
      },
      onError: (error) => {
        const message = error.response?.data?.message || 'Error al enviar recordatorio semanal'
        enqueueSnackbar(message, { variant: 'error' })
      }
    }
  )
}

// Hook para enviar alerta de vencidas manualmente
export const useSendOverdueAlert = () => {
  const { enqueueSnackbar } = useSnackbar()

  return useMutation(
    () => notificationsAPI.sendOverdueAlert(),
    {
      onSuccess: (data) => {
        console.log('🔍 DEBUG - useSendOverdueAlert - data recibida:', data);
        const { invoiceCount, totalAmount, recipients } = data.data || data
        console.log('🔍 DEBUG - useSendOverdueAlert - valores extraídos:', { invoiceCount, totalAmount, recipients });
        enqueueSnackbar(
          `Alerta enviada: ${invoiceCount} facturas vencidas por $${totalAmount?.toLocaleString('es-CL')} a ${recipients?.length} destinatario(s)`,
          { variant: 'success' }
        )
      },
      onError: (error) => {
        const message = error.response?.data?.message || 'Error al enviar alerta de vencidas'
        enqueueSnackbar(message, { variant: 'error' })
      }
    }
  )
}

// Hook para enviar reporte mensual manualmente
export const useSendMonthlyReport = () => {
  const { enqueueSnackbar } = useSnackbar()

  return useMutation(
    () => notificationsAPI.sendMonthlyReport(),
    {
      onSuccess: (data) => {
        console.log('🔍 DEBUG - useSendMonthlyReport - data recibida:', data);
        const { totalInvoices, totalAmount, paidInvoices, pendingInvoices, overdueInvoices, month, year, recipients } = data.data || data
        console.log('🔍 DEBUG - useSendMonthlyReport - valores extraídos:', { totalInvoices, totalAmount, paidInvoices, pendingInvoices, overdueInvoices, month, year, recipients });
        enqueueSnackbar(
          `Reporte mensual enviado: ${totalInvoices} facturas (${paidInvoices} pagadas, ${pendingInvoices} pendientes, ${overdueInvoices} vencidas) por un total de $${totalAmount?.toLocaleString('es-CL') || 0} (${month} ${year}) a ${recipients?.length} destinatario(s)`,
          { variant: 'success' }
        )
      },
      onError: (error) => {
        const message = error.response?.data?.message || 'Error al enviar reporte mensual'
        enqueueSnackbar(message, { variant: 'error' })
      }
    }
  )
}

// Hook principal que combina todas las funcionalidades
export const useNotificationManagement = (onFormSuccess) => {
  const configs = useNotificationConfigs()
  const createConfig = useCreateNotificationConfig(onFormSuccess)
  const updateConfig = useUpdateNotificationConfig(onFormSuccess)
  const deleteConfig = useDeleteNotificationConfig()
  const sendTest = useSendTestNotification()
  const sendWeeklyReminder = useSendWeeklyReminder()
  const sendOverdueAlert = useSendOverdueAlert()
  const sendMonthlyReport = useSendMonthlyReport()

  console.log('🔍 DEBUG - useNotificationManagement - configs:', {
    data: configs.data,
    isLoading: configs.isLoading,
    error: configs.error,
    status: configs.status,
    isFetching: configs.isFetching
  });

  return {
    // Data
    configs: configs.data || [], // configs ya es el array procesado por select
    isLoading: configs.isLoading,
    error: configs.error,
    
    // Actions
    createConfig: createConfig.mutate,
    updateConfig: updateConfig.mutate,
    deleteConfig: deleteConfig.mutate,
    sendTest: sendTest.mutate,
    sendWeeklyReminder: sendWeeklyReminder.mutate,
    sendOverdueAlert: sendOverdueAlert.mutate,
    sendMonthlyReport: sendMonthlyReport.mutate,
    
    // Loading states
    isCreating: createConfig.isLoading,
    isUpdating: updateConfig.isLoading,
    isDeleting: deleteConfig.isLoading,
    isSendingTest: sendTest.isLoading,
    isSendingWeekly: sendWeeklyReminder.isLoading,
    isSendingOverdue: sendOverdueAlert.isLoading,
    isSendingMonthly: sendMonthlyReport.isLoading,
    
    // Utilities
    refetch: configs.refetch
  }
}

export default useNotificationManagement
